import { useRef, useState } from 'react';
import { CapturePanel } from './components/CapturePanel';
import { EditorDeck, EditorDeckFallback, RecorderDeck } from './components/Deck';
import { ExportPanel, type ExportUiState } from './components/ExportPanel';
import { Header } from './components/Header';
import { SourcesPanel } from './components/SourcesPanel';
import { Stage, type StageMode } from './components/Stage';
import { Timeline } from './components/Timeline';
import { TrimPanel } from './components/TrimPanel';
import { MicMonitor } from './lib/audio';
import { probeCapabilities, type CapabilityReport } from './lib/codecs';
import { listDevices, openMicrophone, openWebcam, stopStream, type DeviceOption } from './lib/devices';
import { ScreenRecorder, describeError } from './lib/engine';
import { ExportCancelled, exportRecording } from './lib/exporter';
import { useMountEffect, useStore } from './lib/hooks';
import { defaultSettings, type Settings } from './settings';

interface TrimRange {
  inUs: number;
  outUs: number;
}

interface Flash {
  tone: 'warn' | 'ok';
  text: string;
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function App() {
  const [engine] = useState(() => new ScreenRecorder());
  const state = useStore(engine.state);

  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [devices, setDevices] = useState<{ mics: DeviceOption[]; cams: DeviceOption[] }>({
    mics: [],
    cams: [],
  });
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const [webcamStream, setWebcamStream] = useState<MediaStream | null>(null);
  const [micMonitor, setMicMonitor] = useState<MicMonitor | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  const [support, setSupport] = useState<CapabilityReport | null>(null);
  const [trimDraft, setTrimDraft] = useState<(TrimRange & { id: string }) | null>(null);
  const [exportState, setExportState] = useState<ExportUiState>({ status: 'idle' });

  const abortRef = useRef<AbortController | null>(null);
  const resourcesRef = useRef<{
    mic: MediaStream | null;
    cam: MediaStream | null;
    monitor: MicMonitor | null;
  }>({ mic: null, cam: null, monitor: null });

  const update = (patch: Partial<Settings>) => setSettings((prev) => ({ ...prev, ...patch }));

  const recording = state.recording;
  // Derived, not synchronized: a new take owns a fresh default range.
  const trim: TrimRange | null = recording
    ? trimDraft && trimDraft.id === recording.id
      ? { inUs: trimDraft.inUs, outUs: trimDraft.outUs }
      : { inUs: 0, outUs: recording.durationUs }
    : null;

  // The editor opens as soon as the preview exists; the filmstrip and waveform
  // finish indexing in the background.
  // The editor opens as soon as a take exists — even one too large for an
  // in-memory preview, which still has to be exportable.
  const editor = Boolean(recording && trim);
  const busy = state.phase === 'arming' || state.phase === 'recording' || state.phase === 'paused';

  const refreshDevices = async () => {
    try {
      const [mics, cams] = await Promise.all([listDevices('audioinput'), listDevices('videoinput')]);
      setDevices({ mics, cams });
    } catch {
      // enumeration is best effort
    }
  };

  useMountEffect(() => {
    let alive = true;
    void probeCapabilities().then((report) => {
      if (alive) setSupport(report);
    });
    void refreshDevices();
    return () => {
      alive = false;
      abortRef.current?.abort();
      void resourcesRef.current.monitor?.close();
      stopStream(resourcesRef.current.mic);
      stopStream(resourcesRef.current.cam);
    };
  });

  const setTrim = (next: TrimRange) => {
    if (!recording) return;
    setTrimDraft({ id: recording.id, ...next });
  };

  const toggleMic = async (enabled: boolean) => {
    if (!enabled) {
      await resourcesRef.current.monitor?.close();
      resourcesRef.current.monitor = null;
      setMicMonitor(null);
      stopStream(resourcesRef.current.mic);
      resourcesRef.current.mic = null;
      setMicStream(null);
      update({ micEnabled: false });
      return;
    }
    try {
      const stream = await openMicrophone(settings.micDeviceId || undefined);
      resourcesRef.current.mic = stream;
      setMicStream(stream);
      update({ micEnabled: true });
      const monitor = await MicMonitor.create(stream);
      resourcesRef.current.monitor = monitor;
      setMicMonitor(monitor);
      await refreshDevices();
    } catch (error) {
      setFlash({ tone: 'warn', text: describeError(error) });
      update({ micEnabled: false });
    }
  };

  const toggleWebcam = async (enabled: boolean) => {
    if (!enabled) {
      stopStream(resourcesRef.current.cam);
      resourcesRef.current.cam = null;
      setWebcamStream(null);
      update({ webcamEnabled: false });
      return;
    }
    try {
      const stream = await openWebcam(settings.webcamDeviceId || undefined);
      resourcesRef.current.cam = stream;
      setWebcamStream(stream);
      update({ webcamEnabled: true });
      await refreshDevices();
    } catch (error) {
      setFlash({ tone: 'warn', text: describeError(error) });
      update({ webcamEnabled: false });
    }
  };

  const switchMicDevice = async (deviceId: string) => {
    update({ micDeviceId: deviceId });
    if (!settings.micEnabled) return;
    await toggleMic(false);
    await toggleMic(true);
  };

  const switchWebcamDevice = async (deviceId: string) => {
    update({ webcamDeviceId: deviceId });
    if (!settings.webcamEnabled) return;
    await toggleWebcam(false);
    await toggleWebcam(true);
  };

  // Gain changes apply live while a take is running, and persist for the next.
  const setMicGain = (value: number) => {
    update({ micGain: value });
    engine.setGains(value, settings.systemGain);
  };

  const setSystemGain = (value: number) => {
    update({ systemGain: value });
    engine.setGains(settings.micGain, value);
  };

  const toggleLoop = () => {
    const next = !settings.loopTrim;
    update({ loopTrim: next });
    state.previewPlayer?.setLooping(next);
  };

  const startRecording = async () => {
    setFlash(null);
    setExportState({ status: 'idle' });
    await resourcesRef.current.monitor?.close();
    resourcesRef.current.monitor = null;
    setMicMonitor(null);

    await engine.start({
      fps: settings.fps,
      quality: settings.quality,
      micStream: settings.micEnabled ? micStream : null,
      webcamStream: settings.webcamEnabled ? webcamStream : null,
      micGain: settings.micGain,
      systemGain: settings.systemGain,
      wantSystemAudio: settings.systemAudio,
      overlay: settings.webcamEnabled ? settings.overlay : null,
      countdownMs: settings.countdownMs,
      hideOwnTab: settings.hideOwnTab,
    });
  };

  const runExport = async () => {
    if (!recording || !trim) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setExportState({ status: 'running', progress: { stage: 'planning', ratio: 0 } });
    try {
      const result = await exportRecording(
        recording,
        {
          container: settings.container,
          mode: settings.mode,
          inUs: trim.inUs,
          outUs: trim.outUs,
          scale: settings.scale,
          bitrate:
            settings.bitrateMbps !== null
              ? settings.bitrateMbps * 1_000_000
              : Math.max(2_000_000, recording.videoBitrate || 12_000_000),
          includeAudio: settings.includeAudio,
        },
        {
          signal: controller.signal,
          onProgress: (progress) => setExportState({ status: 'running', progress }),
        },
      );
      download(result.blob, result.filename);
      setExportState({ status: 'done', result });
    } catch (error) {
      if (error instanceof ExportCancelled) setExportState({ status: 'idle' });
      else setExportState({ status: 'error', message: describeError(error) });
    } finally {
      abortRef.current = null;
    }
  };

  const stageMode: StageMode = busy
    ? 'live'
    : state.previewPlayer
      ? 'preview'
      : recording
        ? 'unavailable'
        : 'idle';

  const meterStore = micMonitor?.meters ?? engine.meters;

  const keyHandler = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (target && /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;

    const player = state.previewPlayer;
    if (event.key === 'Escape' && exportState.status === 'running') {
      abortRef.current?.abort();
      return;
    }
    if ((event.key === 'r' || event.key === 'R') && !event.metaKey && !event.ctrlKey) {
      if (busy) void engine.stop();
      else void startRecording();
      return;
    }
    if (!player || !recording || !trim) return;

    const nudge = event.shiftKey ? 1_000_000 : recording.frameUs;
    switch (event.key) {
      case ' ':
        event.preventDefault();
        player.toggle();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        player.pause();
        player.seekUs(player.store.getSnapshot().currentUs - nudge);
        break;
      case 'ArrowRight':
        event.preventDefault();
        player.pause();
        player.seekUs(player.store.getSnapshot().currentUs + nudge);
        break;
      case 'i':
      case 'I': {
        const at = player.store.getSnapshot().currentUs;
        setTrim({ inUs: Math.min(at, trim.outUs - recording.frameUs), outUs: trim.outUs });
        break;
      }
      case 'o':
      case 'O': {
        const at = player.store.getSnapshot().currentUs;
        setTrim({ inUs: trim.inUs, outUs: Math.max(at, trim.inUs + recording.frameUs) });
        break;
      }
      default:
        break;
    }
  };
  const keyRef = useRef(keyHandler);
  keyRef.current = keyHandler;
  useMountEffect(() => {
    const listener = (event: KeyboardEvent) => keyRef.current(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  });

  const notice = state.error ?? state.notice ?? (flash ? flash.text : null);
  const noticeTone = state.error || (flash && flash.tone === 'warn') ? 'warn' : 'info';

  return (
    <div className="app">
      <Header state={state} />

      {support && !support.supported ? (
        <div className="banner banner--warn">
          <strong>This browser can’t record.</strong> {support.reason}
        </div>
      ) : null}
      {notice ? (
        <div className={`banner banner--${noticeTone}`}>
          <span>{notice}</span>
          <button
            type="button"
            className="banner__close"
            aria-label="Dismiss"
            onClick={() => {
              setFlash(null);
              if (state.notice) engine.state.setState({ notice: null });
              if (state.error) engine.discard();
            }}
          >
            ✕
          </button>
        </div>
      ) : null}

      <main className="workspace">
        <div className="workspace__main">
          <Stage
            engine={engine}
            player={state.previewPlayer}
            mode={stageMode}
            armed={Boolean(state.capture)}
            countdown={state.countdown}
          />

          {editor && recording && trim ? (
            state.previewPlayer ? (
              <EditorDeck
                player={state.previewPlayer}
                durationUs={recording.durationUs}
                onNewTake={() => engine.discard()}
                loop={settings.loopTrim}
                onToggleLoop={toggleLoop}
                frameUs={recording.frameUs}
              />
            ) : (
              <EditorDeckFallback
                durationUs={recording.durationUs}
                onNewTake={() => engine.discard()}
              />
            )
          ) : (
            <RecorderDeck
              state={state}
              meterStore={meterStore}
              onRecord={() => void startRecording()}
              onStop={() => void engine.stop()}
              onPause={() => engine.pause()}
              onResume={() => engine.resume()}
              onDiscard={() => engine.discard()}
              canRecord={Boolean(support?.supported)}
            />
          )}

          {recording && trim ? (
            state.previewPlayer ? (
              <Timeline
                key={recording.id}
                recording={recording}
                player={state.previewPlayer}
                trim={trim}
                onChange={setTrim}
                snap={settings.snapToKeyframes}
                indexing={state.indexProgress !== null}
              />
            ) : (
              <div className="timeline timeline--empty">
                <span className="timeline__note">
                  {state.indexProgress
                    ? `Indexing ${state.indexProgress.stage}…`
                    : 'Timeline unavailable for this take'}
                </span>
              </div>
            )
          ) : null}
        </div>

        <aside className="rail">
          {editor && recording && trim ? (
            <>
              <TrimPanel
                recording={recording}
                trim={trim}
                onChange={setTrim}
                settings={settings}
                update={update}
                player={state.previewPlayer}
              />
              <ExportPanel
                recording={recording}
                trim={trim}
                settings={settings}
                update={update}
                exportState={exportState}
                onExport={() => void runExport()}
                onCancel={() => abortRef.current?.abort()}
              />
            </>
          ) : (
            <>
              <SourcesPanel
                settings={settings}
                update={update}
                devices={devices}
                micStream={micStream}
                webcamStream={webcamStream}
                micMeterStore={meterStore}
                onToggleMic={(enabled) => void toggleMic(enabled)}
                onToggleWebcam={(enabled) => void toggleWebcam(enabled)}
                onMicDevice={(deviceId) => void switchMicDevice(deviceId)}
                onWebcamDevice={(deviceId) => void switchWebcamDevice(deviceId)}
                onMicGain={setMicGain}
                onSystemGain={setSystemGain}
                busy={busy}
              />
              <CapturePanel settings={settings} update={update} busy={busy} />
            </>
          )}
          <ShortcutCard />
        </aside>
      </main>

      <footer className="colophon">
        <span>
          Capture, encoding and muxing all happen in this tab — nothing is uploaded. Tabs need
          permission each time; Chrome, Edge, Safari 16.4+ and Firefox 130+ can encode H.264.
        </span>
        <span className="colophon__marks">
          <span>WebCodecs</span>
          <span>getDisplayMedia</span>
          <span>AudioWorklet</span>
        </span>
      </footer>
    </div>
  );
}

function ShortcutCard() {
  return (
    <section className="panel panel--quiet">
      <header className="panel__head">
        <h2 className="panel__label">Keyboard</h2>
      </header>
      <div className="panel__body">
        <dl className="shortcuts">
          <div>
            <dt>space</dt>
            <dd>play / pause the take</dd>
          </div>
          <div>
            <dt>← →</dt>
            <dd>step one frame (shift: one second)</dd>
          </div>
          <div>
            <dt>i / o</dt>
            <dd>set in / out at the playhead</dd>
          </div>
          <div>
            <dt>r</dt>
            <dd>start or stop recording</dd>
          </div>
          <div>
            <dt>esc</dt>
            <dd>cancel an export</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
